/**
 * API client for the ops console. All requests use master user auth (same cookies as main app).
 * All data is live from the backend (clubs, meetings, submissions); no seed or mock data.
 *
 * Admin API contract:
 * - Base URL: ${API}/admin (e.g. /api/admin)
 * - List responses and PATCH bodies use snake_case (e.g. club_id, hero_image; meetings use status for publish workflow).
 * - Clubs list: GET /clubs?city_id=&limit= — returns ClubResponse[] (id, name, city, hero_image, profile_picture, description, home_base_address, home_base_description, instagram, website, claim_status, internal_notes, …).
 * - Meetings list: GET /meetings?city_id=&limit= — returns raw meeting docs (id, club_id, club_name, name, scheduled_date, address, slot, reliability, status, recurrence_pattern, event_type, difficulty, what_to_expect, paces, images, …).
 */
import axios from "axios";
import { API } from "../constants/api";
import { getAccessToken } from "../utils/authTokenStorage";

const client = axios.create({
  baseURL: `${API}/admin`,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    // Don't toast on 401: when the user is in the console as master-by-email (cookies not sent cross-origin),
    // every admin request 401s and would spam "Invalid or expired admin session". Let the UI show empty/error state instead.
    return Promise.reject(err);
  }
);

/** Pull a quoted filename out of a Content-Disposition header. Used by downloaders. */
function _extractFilename(header) {
  if (!header || typeof header !== "string") return null;
  const m = header.match(/filename="?([^";]+)"?/i);
  return m ? m[1] : null;
}

/** Trigger a browser file save for a Blob with a given filename. */
export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download an arbitrary image URL with a given filename.
 *
 * Works for:
 *   - Same-origin / backend-hosted URLs (stock library)
 *   - Cross-origin URLs with permissive CORS (GCP public bucket)
 *
 * If fetch fails (CORS, network), falls back to opening the URL in a new tab.
 */
export async function downloadImageFromUrl(url, filename) {
  try {
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    triggerBlobDownload(blob, filename);
    return true;
  } catch (e) {
    // CORS or network blocked the fetch — open the file so the user can save manually
    window.open(url, "_blank", "noopener,noreferrer");
    return false;
  }
}

/** Short-lived cache for coverage stats so city views can share one request per city+range. TTL 90s. */
const COVERAGE_CACHE_TTL_MS = 90 * 1000;
const coverageCache = { key: null, data: null, ts: 0 };
function getCoverageCacheKey(cityId, rangeKey) {
  return `${cityId || "__all__"}:${rangeKey ?? ""}`;
}
function getCachedCoverage(cityId, rangeKey) {
  const key = getCoverageCacheKey(cityId, rangeKey);
  if (coverageCache.key === key && Date.now() - coverageCache.ts < COVERAGE_CACHE_TTL_MS) {
    return coverageCache.data;
  }
  return null;
}
function setCachedCoverage(cityId, rangeKey, data) {
  coverageCache.key = getCoverageCacheKey(cityId, rangeKey);
  coverageCache.data = data;
  coverageCache.ts = Date.now();
}

/** Short-lived cache for clubs list (keyed by city_id + limit). TTL 90s. */
const LIST_CACHE_TTL_MS = 90 * 1000;
const clubsListCache = { key: null, data: null, ts: 0 };
const meetingsListCache = { key: null, data: null, ts: 0 };

function getClubsListCacheKey(params) {
  const cityId = params?.city_id ?? "__all__";
  const limit = params?.limit ?? 200;
  return `${cityId}:${limit}`;
}

function getCachedClubsList(params) {
  const key = getClubsListCacheKey(params);
  if (clubsListCache.key === key && Date.now() - clubsListCache.ts < LIST_CACHE_TTL_MS) {
    return clubsListCache.data;
  }
  return null;
}

function setCachedClubsList(params, data) {
  clubsListCache.key = getClubsListCacheKey(params);
  clubsListCache.data = data;
  clubsListCache.ts = Date.now();
}

/** Clear list cache so refetch after PATCH/create/delete sees fresh data (TTL alone would keep stale rows). */
function invalidateClubsListCache() {
  clubsListCache.key = null;
  clubsListCache.data = null;
  clubsListCache.ts = 0;
}

function invalidateMeetingsListCache() {
  meetingsListCache.key = null;
  meetingsListCache.data = null;
  meetingsListCache.ts = 0;
}

function getMeetingsListCacheKey(params) {
  const cityId = params?.city_id ?? "__all__";
  const limit = params?.limit ?? 200;
  const rangeKey = params?.range_key ?? "";
  const skip = params?.skip ?? 0;
  return `${cityId}:${rangeKey}:${limit}:${skip}`;
}

function getCachedMeetingsList(params) {
  const key = getMeetingsListCacheKey(params);
  if (meetingsListCache.key === key && Date.now() - meetingsListCache.ts < LIST_CACHE_TTL_MS) {
    return meetingsListCache.data;
  }
  return null;
}

function setCachedMeetingsList(params, data) {
  meetingsListCache.key = getMeetingsListCacheKey(params);
  meetingsListCache.data = data;
  meetingsListCache.ts = Date.now();
}

export const consoleApi = {
  auth: {
    login: (adminKey) =>
      client.post("/login", { admin_key: adminKey }).then((r) => r.data),
    logout: () => client.post("/logout").then((r) => r.data),
    me: () => client.get("/me").then((r) => r.data),
  },

  cities: {
    list: (params) => client.get("/cities", { params }).then((r) => r.data),
    get: (id) => client.get(`/cities/${id}`).then((r) => r.data),
    create: (body) => client.post("/cities", body).then((r) => r.data),
    update: (id, body) => client.patch(`/cities/${id}`, body).then((r) => r.data),
    delete: (id) => client.delete(`/cities/${id}`),
  },

  clubs: {
    list: (params) => {
      const safeParams = params && typeof params === "object" ? params : {};
      const cached = getCachedClubsList(safeParams);
      if (cached !== null) return Promise.resolve(cached);
      return client.get("/clubs", { params: safeParams }).then((r) => {
        setCachedClubsList(safeParams, r.data);
        return r.data;
      });
    },
    get: (id) => client.get(`/clubs/${id}`).then((r) => r.data),
    issueOrganizerClaimCode: (clubId) =>
      client.post(`/clubs/${encodeURIComponent(clubId)}/organizer-claim-code`).then((r) => {
        invalidateClubsListCache();
        return r.data;
      }),
    getClaimMetrics: (cityId) =>
      client.get("/clubs/claim-metrics", { params: { city_id: cityId } }).then((r) => r.data),
    getOrganizerId: (clubId) =>
      client.get(`/clubs/${clubId}/organizer-id`).then((r) => r.data?.organizer_id ?? null),
    getMembers: (clubId, limit = 200, offset = 0) =>
      client.get(`/clubs/${clubId}/club_members`, { params: { limit, offset } }).then((r) => r.data),
    create: (body) =>
      client.post("/clubs", body).then((r) => {
        invalidateClubsListCache();
        return r.data;
      }),
    update: (id, body) =>
      client.patch(`/clubs/${id}`, body).then((r) => {
        invalidateClubsListCache();
        return r.data;
      }),
    delete: (id) =>
      client.delete(`/clubs/${id}`).then((r) => {
        invalidateClubsListCache();
        invalidateMeetingsListCache();
        return r;
      }),
  },

  /** Create runclub user + club (same as mobile signup: user first, then club). */
  onboardClub: (body) =>
    client.post("/onboard-club", body).then((r) => {
      invalidateClubsListCache();
      return r.data;
    }),

  /** Upload club image (hero or profile) to GCP (clubcard_images), same as mobile. Returns { url }. */
  uploadClubImage: (clubId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return client
      .post(`/upload/club-image?club_id=${encodeURIComponent(clubId)}`, formData, {
        headers: { "Content-Type": false },
        timeout: 60000,
      })
      .then((r) => r.data);
  },

  /** Fetch image from URL, upscale if small (e.g. Strava 124x124), upload to GCP, return { url }. For import flow. */
  uploadClubImageFromUrl: (clubId, imageUrl) =>
    client
      .post("/upload/club-image-from-url", { image_url: imageUrl, club_id: clubId })
      .then((r) => r.data),

  /** Upload run (meeting) image to GCP (meeting_image), same as mobile. Returns { url }. */
  uploadMeetingImage: (meetingId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return client
      .post(`/upload/meeting-image?meeting_id=${encodeURIComponent(meetingId)}`, formData, {
        headers: { "Content-Type": false },
        timeout: 60000,
      })
      .then((r) => r.data);
  },

  /** Fetch image from URL, upload to GCP as meeting_image. Returns { url }. */
  uploadMeetingImageFromUrl: (meetingId, imageUrl) =>
    client
      .post("/upload/meeting-image-from-url", { image_url: imageUrl, meeting_id: meetingId })
      .then((r) => r.data),

  meetings: {
    list: (params) => {
      const safeParams = params && typeof params === "object" ? params : {};
      const cached = getCachedMeetingsList(safeParams);
      if (cached !== null) return Promise.resolve(cached);
      return client.get("/meetings", { params: safeParams }).then((r) => {
        setCachedMeetingsList(safeParams, r.data);
        return r.data;
      });
    },
    get: (id) => client.get(`/meetings/${id}`).then((r) => r.data),
    create: (body) =>
      client.post("/meetings", body).then((r) => {
        invalidateMeetingsListCache();
        return r.data;
      }),
    /**
     * @param {string} id
     * @param {object} body
     * @param {{ updateAllFutureRuns?: boolean }} [options] If `updateAllFutureRuns` is set, sent as
     *   `update_all_instances` (recurring series scope). Omit to use server default (true for admin).
     */
    update: (id, body, options = {}) => {
      const params = {};
      if (typeof options.updateAllFutureRuns === "boolean") {
        params.update_all_instances = options.updateAllFutureRuns;
      }
      return client
        .patch(`/meetings/${encodeURIComponent(id)}`, body, { params })
        .then((r) => {
          invalidateMeetingsListCache();
          return r.data;
        });
    },
    delete: (id, options = {}) => {
      const params = {};
      if (options.deleteEntireSeries === true) {
        params.delete_entire_series = true;
      }
      return client.delete(`/meetings/${encodeURIComponent(id)}`, { params }).then((r) => {
        invalidateMeetingsListCache();
        return r;
      });
    },
    /**
     * Backfill latitude/longitude/location for meetings missing coordinates (admin maintenance).
     * body: { city_id?, club_id?, limit?, dry_run? } — specify city_id xor club_id unless dry_run.
     */
    regeocodeMissingCoordinates: (body) =>
      client
        .post("/meetings/regeocode-missing-coordinates", body && typeof body === "object" ? body : {}, {
          timeout: 300000,
        })
        .then((r) => {
          invalidateMeetingsListCache();
          return r.data;
        }),
  },

  submissions: {
    list: (params) => client.get("/submissions", { params }).then((r) => r.data),
    count: (params) => client.get("/submissions/count", { params }).then((r) => r.data),
    /** All status counts in one request: { submitted, review, draft, approved, rejected, total }. */
    counts: (params) => client.get("/submissions/counts", { params }).then((r) => r.data),
    get: (id) => client.get(`/submissions/${id}`).then((r) => r.data),
    create: (body) => client.post("/submissions", body).then((r) => r.data),
    update: (id, body) => client.patch(`/submissions/${id}`, body).then((r) => r.data),
    delete: (id) => client.delete(`/submissions/${id}`),
  },

  stats: {
    coverage(cityId, params = {}) {
      const safeParams = params && typeof params === "object" ? params : {};
      const rangeKey = safeParams.range_key ?? "";
      const cached = getCachedCoverage(cityId, rangeKey);
      if (cached !== null) return Promise.resolve(cached);
      const requestParams = { ...safeParams };
      if (cityId) requestParams.city_id = cityId;
      return client.get("/stats/coverage", { params: requestParams }).then((r) => {
        setCachedCoverage(cityId, rangeKey, r.data);
        return r.data;
      });
    },
    users: (params) => client.get("/stats/users", { params: params ?? {} }).then((r) => r.data),
    networkScoreboard: () => client.get("/stats/network/scoreboard").then((r) => r.data),
    networkSummary: () => client.get("/stats/network/summary").then((r) => r.data),
    networkOverview: (params) =>
      client.get("/stats/network/overview", { params: params ?? {} }).then((r) => r.data),
    networkIntelligence: (params) =>
      client.get("/stats/network/intelligence", { params: params ?? {} }).then((r) => r.data),
    export: (cityId) =>
      client.get("/stats/export", { params: { city_id: cityId } }).then((r) => r.data),
    qualityChecks: (cityId) =>
      client.get("/stats/quality-checks", { params: cityId ? { city_id: cityId } : {} }).then((r) => r.data),
  },

  /** Console assistant (LLM-backed chatbot). Longer timeout for LLM + tools. */
  assistant: {
    chat: (body) =>
      client.post("/assistant/chat", body, { timeout: 60000 }).then((r) => r.data),
  },

  /** Admin-to-developer feedback (ops console). Images stored in DB as base64, not GCP. */
  feedback: {
    list: (params) => client.get("/feedback", { params }).then((r) => r.data),
    get: (id) => client.get(`/feedback/${id}`).then((r) => r.data),
    create: (body) => client.post("/feedback", body).then((r) => r.data),
    update: (id, body) => client.patch(`/feedback/${id}`, body).then((r) => r.data),
    addNote: (id, body) => client.post(`/feedback/${id}/notes`, body).then((r) => r.data),
    delete: (id) => client.delete(`/feedback/${id}`),
    stats: () => client.get("/feedback/stats").then((r) => r.data),
  },

  /** Content Hub — marketing briefs, AI copy, screenshot capture, render. */
  content: {
    listBriefs: (params) => client.get("/content/briefs", { params }).then((r) => r.data),
    getBrief: (id) => client.get(`/content/briefs/${id}`).then((r) => r.data),
    createBrief: (body) => client.post("/content/briefs", body).then((r) => r.data),
    updateBrief: (id, body) => client.patch(`/content/briefs/${id}`, body).then((r) => r.data),
    deleteBrief: (id) => client.delete(`/content/briefs/${id}`),
    generateCopy: (id, body) =>
      client
        .post(`/content/briefs/${id}/generate-copy`, body ?? { regenerate: false }, { timeout: 120000 })
        .then((r) => r.data),
    regenerateField: (id, slideIndex, body) =>
      client
        .post(`/content/briefs/${id}/slides/${slideIndex}/regenerate-field`, body, { timeout: 60000 })
        .then((r) => r.data),
    generateVariations: (id, body) =>
      client
        .post(`/content/briefs/${id}/variations`, body ?? { count: 3 }, { timeout: 300000 })
        .then((r) => r.data),
    generateReel: (id, body) =>
      client
        .post(`/content/briefs/${id}/reel`, body ?? {}, { timeout: 300000 })
        .then((r) => r.data),
    /** Bundled royalty-free music tracks for the reel music picker, grouped by vibe. */
    listAudio: () => client.get(`/content/audio-library`).then((r) => r.data),
    /** Fetch one track ("<vibe>/<file>") as a Blob for in-picker preview (auth via client). */
    getAudioBlob: (trackId) =>
      client.get(`/content/audio/${trackId}`, { responseType: "blob" }).then((r) => r.data),
    animateSlide: (id, slideIndex, body) =>
      client
        .post(`/content/briefs/${id}/slides/${slideIndex}/animate`, body ?? {}, { timeout: 300000 })
        .then((r) => r.data),
    uploadAsset: (id, formData) =>
      client
        .post(`/content/briefs/${id}/assets`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        })
        .then((r) => r.data),
    attachScreenshotAsAsset: (briefId, screenshotId) =>
      client
        .post(`/content/briefs/${briefId}/assets/from-screenshot`, {
          screenshot_id: screenshotId,
        })
        .then((r) => r.data),
    capture: (id, body) =>
      client.post(`/content/briefs/${id}/capture`, body, { timeout: 30000 }).then((r) => r.data),
    mobileCaptureStatus: () =>
      client.get("/content/mobile-capture/status", { timeout: 15000 }).then((r) => r.data),
    captureMobile: (id, body) =>
      client.post(`/content/briefs/${id}/capture-mobile`, body, { timeout: 60000 }).then((r) => r.data),
    captureMobileBatch: (id, body) =>
      client.post(`/content/briefs/${id}/capture-mobile/batch`, body ?? {}, { timeout: 180000 }).then((r) => r.data),
    runScreenshotBot: (body) =>
      client.post("/content/screenshot-bot/run", body ?? {}, { timeout: 600000 }).then((r) => r.data),
    generateImage: (id, body) =>
      client.post(`/content/briefs/${id}/generate-image`, body, { timeout: 320000 }).then((r) => r.data),
    render: (id, body) =>
      client.post(`/content/briefs/${id}/render`, body ?? {}, { timeout: 120000 }).then((r) => r.data),
    health: () => client.get("/content/health").then((r) => r.data),
    stockLibrary: () => client.get("/content/stock-library").then((r) => r.data),
    upcomingMeetings: (params) =>
      client.get("/content/meetings/upcoming", { params }).then((r) => r.data),
    downloadRender: (id, slideIndex) =>
      client
        .get(`/content/briefs/${id}/renders/${slideIndex}/download`, {
          responseType: "blob",
          timeout: 60000,
        })
        .then((r) => ({
          blob: r.data,
          filename: _extractFilename(r.headers?.["content-disposition"]) || `slide-${slideIndex}.png`,
        })),
    downloadRendersZip: (id) =>
      client
        .get(`/content/briefs/${id}/renders.zip`, {
          responseType: "blob",
          timeout: 120000,
        })
        .then((r) => ({
          blob: r.data,
          filename: _extractFilename(r.headers?.["content-disposition"]) || `runcards-slides.zip`,
        })),
    library: (params) => client.get("/content/library", { params }).then((r) => r.data),
    listScreenshots: (params) =>
      client.get("/content/screenshots", { params }).then((r) => r.data),
    uploadScreenshot: (formData) =>
      client
        .post("/content/screenshots", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        })
        .then((r) => r.data),
    deleteScreenshot: (id) => client.delete(`/content/screenshots/${id}`),
    updateScreenshot: (id, body) =>
      client.patch(`/content/screenshots/${id}`, body).then((r) => r.data),

    /** Media bank — videos / reels / motion graphics. */
    listMedia: (params) =>
      client.get("/content/media", { params }).then((r) => r.data),
    uploadMedia: (formData) =>
      client
        .post("/content/media", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        })
        .then((r) => r.data),
    deleteMedia: (id) => client.delete(`/content/media/${id}`),
    updateMedia: (id, body) =>
      client.patch(`/content/media/${id}`, body).then((r) => r.data),
    backfillMedia: () =>
      client
        .post("/content/media/backfill", {}, { timeout: 120000 })
        .then((r) => r.data),

    /** Studio — direct compose-and-post to the connected Instagram accounts. */
    studioAccounts: () =>
      client.get("/content/studio/accounts", { timeout: 30000 }).then((r) => r.data),
    studioHistory: () =>
      client.get("/content/studio/history", { timeout: 45000 }).then((r) => r.data),
    studioPost: (formData) =>
      client
        .post("/content/studio/post", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        })
        .then((r) => r.data),
  },
};

export default consoleApi;
